import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { showError } from '../utils/sweetAlert';
import AddToProjectModal from './AddToProjectModal';
import { Skeleton } from './ui/skeleton';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const PAGE_SIZE = 10;
const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created (Newest)' },
  { value: 'created_at_asc', label: 'Created (Oldest)' },
  { value: 'payback_years', label: 'Payback (Shortest)' },
  { value: 'payback_years_desc', label: 'Payback (Longest)' },
  { value: 'priority', label: 'Priority (High→Low)' },
  { value: 'priority_asc', label: 'Priority (Low→High)' }
];

const ProjectsList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  const [allProjects, setAllProjects] = useState([]); // Store all projects for client-side pagination
  const [projects, setProjects] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false); // Track if we've attempted to fetch

  const [filters, setFilters] = useState({ status: '', priority: '', userId: '' });
  const [sort, setSort] = useState('created_at');
  const [pagination, setPagination] = useState({ page: 1 });
  
  // Get unique users for admin filter
  const [availableUsers, setAvailableUsers] = useState([]);
  const isAdmin = user?.role === 'admin';

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [pendingRecommendation, setPendingRecommendation] = useState(null);
  const [modalOpened, setModalOpened] = useState(false); // Track if modal was already opened

  // Initial fetch - wait for authentication
  useEffect(() => {
    // Only fetch if authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated && !hasFetched && user) {
      // Add a small delay to ensure cookies are properly set after authentication
      // This fixes the issue where first load doesn't get projects due to cookie timing
      const fetchTimer = setTimeout(() => {
        setHasFetched(true);
        fetchProjects(filters, sort);
      }, 100); // Small delay to ensure cookies are available
      
      return () => clearTimeout(fetchTimer);
    }
    // eslint-disable-next-line
  }, [authLoading, isAuthenticated, hasFetched, user]);

  // Refetch when filters or sort change (after initial fetch)
  useEffect(() => {
    // Only refetch if we've already done the initial fetch
    if (hasFetched && !authLoading && isAuthenticated) {
      // Reset to page 1 when filters or sort change
      setPagination({ page: 1 });
      fetchProjects(filters, sort);
    }
    // eslint-disable-next-line
  }, [filters.status, filters.priority, filters.userId, sort, hasFetched, authLoading, isAuthenticated]);

  // Separate effect for handling modal from URL params
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create' && !modalOpened) {
      const storedRec = sessionStorage.getItem('pendingProject');
      if (storedRec) {
        try {
          const rec = JSON.parse(storedRec);
          setPendingRecommendation(rec);
          setShowModal(true);
          setModalOpened(true);
          sessionStorage.removeItem('pendingProject');
          // Remove action from URL to prevent reopening
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('action');
          navigate(`/projects?${newSearchParams.toString()}`, { replace: true });
        } catch (error) {
          logger.error('Error parsing pending project:', error);
        }
      }
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Effect for client-side pagination
  useEffect(() => {
    applyPaginationAndSorting();
    // eslint-disable-next-line
  }, [allProjects, pagination.page, sort]);

  const fetchProjects = async (filtersVal = filters, sortVal = sort, retryCount = 0) => {
    const MAX_RETRIES = 1; // Only retry once for empty responses
    const RETRY_DELAY = 300; // 300ms delay between retries
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      // Remove pagination params since backend doesn't support it - we'll do client-side pagination
      if (filtersVal.status) params.append('status', filtersVal.status);
      if (filtersVal.priority) params.append('priority', filtersVal.priority);
      if (filtersVal.userId && isAdmin) params.append('account_id', filtersVal.userId);

      logger.log(`Fetching projects (attempt ${retryCount + 1}) with params:`, params.toString());
      logger.log('User authenticated:', isAuthenticated, 'User ID:', user?.user_id || user?.id);
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/comprehensive?${params.toString()}`,
        { withCredentials: true }
      );
      
      logger.log('Projects API response status:', response.status);
      logger.log('Projects API response data type:', typeof response.data);
      logger.log('Projects API response data:', JSON.stringify(response.data, null, 2));
      logger.log('Is array?', Array.isArray(response.data));
      
      // Backend returns array directly, not wrapped in object
      let projectsData = [];
      if (Array.isArray(response.data)) {
        projectsData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Check if it's wrapped in an object
        if (Array.isArray(response.data.projects)) {
          projectsData = response.data.projects;
        } else if (Array.isArray(response.data.data)) {
          projectsData = response.data.data;
        } else if (response.data && !Array.isArray(response.data)) {
          // Single project object
          projectsData = [response.data];
        }
      }
      
      logger.log('Parsed projects data:', projectsData.length, 'projects');
      if (projectsData.length > 0) {
        logger.log('First project sample:', JSON.stringify(projectsData[0], null, 2));
      }
      
      // Only retry on empty response if it's the first attempt (might be timing issue)
      // After first retry, if still empty, it's likely there are no projects - don't retry again
      if (projectsData.length === 0 && retryCount === 0 && isAuthenticated && !authLoading) {
        logger.warn(`Empty response on first attempt, retrying once in ${RETRY_DELAY}ms in case of timing issues...`);
        setTimeout(() => {
          fetchProjects(filtersVal, sortVal, retryCount + 1);
        }, RETRY_DELAY);
        return; // Don't set loading to false yet - we're retrying
      }
      
      // If still empty after retry, log it but don't retry again - user might just have no projects
      if (projectsData.length === 0) {
        logger.info('No projects found for user - this is a valid empty state (not retrying further)');
      }
      
      setAllProjects(projectsData);
      setTotalCount(projectsData.length);
      setLoading(false); // Success or final empty state - set loading to false
      
      // Extract unique users for admin filter
      if (isAdmin && projectsData.length > 0) {
        const uniqueUsers = {};
        projectsData.forEach(project => {
          if (project.users && project.users.id) {
            uniqueUsers[project.users.id] = {
              id: project.users.id,
              name: project.users.name || 'Unknown User',
              email: project.users.email || ''
            };
          }
        });
        setAvailableUsers(Object.values(uniqueUsers));
      }
    } catch (error) {
      logger.error('Error fetching projects:', error);
      logger.error('Error response:', error.response?.data);
      logger.error('Error status:', error.response?.status);
      logger.error('Error config:', error.config);
      
      // Retry on 401/403 if we haven't exceeded max retries and auth should be ready
      if ((error.response?.status === 401 || error.response?.status === 403) && retryCount < MAX_RETRIES && !authLoading && isAuthenticated) {
        logger.warn(`Auth error on attempt ${retryCount + 1}, retrying in ${RETRY_DELAY}ms...`);
        setTimeout(() => {
          fetchProjects(filtersVal, sortVal, retryCount + 1);
        }, RETRY_DELAY);
        return;
      }
      
      // Don't show error for 401/403 - user might not be authenticated yet
      if (error.response?.status === 401 || error.response?.status === 403) {
        logger.warn('Authentication required - projects fetch failed');
        // If auth is complete but we got 401/403 after retries, user might need to login
        if (!authLoading && isAuthenticated && retryCount >= MAX_RETRIES) {
          showError('Authentication Error', 'Please log in again to view your projects.');
        }
      } else {
        showError('Error', 'Failed to load projects. Please try again.');
      }
      setAllProjects([]);
      setTotalCount(0);
      // Set loading to false only after all retries are exhausted
      if (retryCount >= MAX_RETRIES) {
        setLoading(false);
      }
    }
    // Note: We don't use finally here because retries return early
    // Loading is set to false either on success or after max retries
  };

  // Apply client-side pagination and sorting
  const applyPaginationAndSorting = () => {
    let sortedProjects = [...allProjects];
    
    // Apply sorting
    if (sort === 'created_at') {
      sortedProjects.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    } else if (sort === 'created_at_asc') {
      sortedProjects.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    } else if (sort === 'payback_years') {
      sortedProjects.sort((a, b) => (a.payback_years || 0) - (b.payback_years || 0));
    } else if (sort === 'payback_years_desc') {
      sortedProjects.sort((a, b) => (b.payback_years || 0) - (a.payback_years || 0));
    } else if (sort === 'priority') {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      sortedProjects.sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0));
    } else if (sort === 'priority_asc') {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      sortedProjects.sort((a, b) => (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0));
    }
    
    // Apply pagination
    const startIndex = (pagination.page - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    setProjects(sortedProjects.slice(startIndex, endIndex));
  };

  const page = pagination.page;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const maxPageButtons = 5;
  const pageStart = Math.max(1, page - Math.floor(maxPageButtons / 2));
  const pageEnd = Math.min(totalPages, pageStart + maxPageButtons - 1);

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'scoping': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'funding': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'approved': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'in_progress': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'commissioning': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'm_and_v': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
      'completed': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      'archived': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    };
    return colors[status] || colors['planned'];
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'medium': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'low': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    };
    return colors[priority] || colors['medium'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setPendingRecommendation(null);
    setModalOpened(false);
    // Refresh projects list - this will show all projects including the new one
    // Don't reset hasFetched, just refetch directly
    fetchProjects(filters, sort);
  };

  // Sort/filter change handlers
  const handleSortChange = (e) => {
    setSort(e.target.value);
    setPagination({ page: 1 });
  };

  const handleStatusFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value }));
    setPagination({ page: 1 });
  };
  const handlePriorityFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, priority: e.target.value }));
    setPagination({ page: 1 });
  };

  // Pagination UI handler
  const setPageBtn = (p) => setPagination({ ...pagination, page: p });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {isAdmin ? 'All User Projects' : 'My Projects'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isAdmin 
                  ? 'Monitor and track all users energy efficiency projects'
                  : 'Track and manage your energy efficiency projects'
                }
              </p>
              {isAdmin && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    Admin View
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {totalCount} total projects
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Filters and Sorting */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Filter by User
                </label>
                <select
                  value={filters.userId}
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, userId: e.target.value }));
                    setPagination({ page: 1 });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[200px]"
                >
                  <option value="">All Users</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={handleStatusFilterChange}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="planned">Planned</option>
                <option value="scoping">Scoping</option>
                <option value="funding">Funding</option>
                <option value="approved">Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="commissioning">Commissioning</option>
                <option value="m_and_v">M&V</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={handlePriorityFilterChange}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Priority</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort By
              </label>
              <select
                value={sort}
                onChange={handleSortChange}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Projects Table/Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
          {authLoading || loading ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  {[...Array(isAdmin ? 8 : 7)].map((_, i) => (
                    <th key={i} className="px-6 py-3"></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(PAGE_SIZE)].map((_, idx) => (
                  <tr key={idx}>
                    {[...Array(isAdmin ? 8 : 7)].map((__, cIdx) => (
                      <td key={cIdx} className="px-6 py-4">
                        <Skeleton className="w-full h-6 rounded bg-gray-100 dark:bg-gray-700" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : projects.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No projects yet</h3>
              <p className="mt-1 text-gray-500 dark:text-gray-400">
                {!isAuthenticated && !authLoading 
                  ? 'Please log in to view your projects.'
                  : 'Get started by adding recommendations to your projects.'}
              </p>
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  Go to Dashboard
                </button>
              )}
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs sm:text-sm md:text-base">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {isAdmin && (
                      <>
                        <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          User
                        </th>
                      </>
                    )}
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Project</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Priority</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cost</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Annual Savings</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payback</th>
                    <th className="px-4 sm:px-6 py-3 text-left font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="">
                  {projects.map((project) => (
                    <tr
                      key={project.id}
                      onClick={() => handleProjectClick(project.id)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-200 dark:border-gray-700"
                    >
                      {isAdmin && (
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold">
                                {project.users?.name ? project.users.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {project.users?.name || 'Unknown User'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {project.users?.email || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="px-4 sm:px-6 py-4">
                        <div className="font-medium text-gray-900 hover:text-green-500 dark:text-white truncate max-w-[160px]">
                          {project.title}
                        </div>
                        {project.description && (
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {project.description}
                          </div>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(project.status)}`}>
                          {project.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(project.priority)}`}>
                          {project.priority?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                        ${project.estimated_cost_aud?.toLocaleString()}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-emerald-600 dark:text-emerald-400">
                        ${project.estimated_savings_aud?.toLocaleString()}/yr
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                        {project.payback_years} yrs
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(project.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Responsive pagination UI, matching your provided design */}
              <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                <div className="flex items-center justify-between">
                  {/* Mobile: Previous/Next */}
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.page + 1, totalPages) }))}
                      disabled={pagination.page === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  {/* Desktop: Numbered pagination */}
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{(pagination.page - 1) * PAGE_SIZE + 1}</span>
                        {' '}to <span className="font-medium">{Math.min(pagination.page * PAGE_SIZE, totalCount)}</span>
                        {' '}of <span className="font-medium">{totalCount}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                          disabled={pagination.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {Array.from({ length: totalPages }, (_, idx) => {
                          const pageNum = idx + 1;
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                          ) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.page === pageNum
                                    ? 'z-10 bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                            return (
                              <span
                                key={pageNum}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.page + 1, totalPages) }))}
                          disabled={pagination.page === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>

            </>
          )}
        </div>

        {/* Add to Project Modal */}
        <AddToProjectModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setPendingRecommendation(null);
            setModalOpened(false);
          }}
          recommendation={pendingRecommendation}
          onSuccess={handleModalSuccess}
        />
      </div>
    </div>
  );
};

export default ProjectsList;
